import React, { PureComponent, ComponentType } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { withPropsOnChange, defaultProps, compose } from 'recompose';

const styles = {
    root: {
        display: 'inline-flex',
    },
};

interface SelectTypeValue {
    type: string;
    value?: unknown;
}

interface SelectTypeOption {
    type: string;
    value?: unknown;
    label?: string;
    group?: string;
    id?: string;
    props?: React.OptionHTMLAttributes<HTMLOptionElement>;
}

interface RawOptionConfig {
    defaultValue?: unknown;
    onChange: (event: unknown) => void;
}

interface SelectTypeProps {
    selectValue: string;
    value: SelectTypeValue;
    options: SelectTypeOption[];
    onChange: (value: { type: string; value?: unknown }) => void;
    label: string;
    rawOption: boolean;
    renderRawOption: (config: RawOptionConfig) => React.ReactNode;
    rawOptionDefault: unknown;
    className?: string;
    [key: string]: unknown;
}

class SelectType extends PureComponent<SelectTypeProps> {
    handleChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
        const { onChange, options } = this.props;
        const match = _.find(options, { id: value });
        if (!match) return;
        onChange(_.pick(match, ['type', 'value']));
    };

    handleRawChange = (event: unknown) => {
        const { onChange } = this.props;
        const value = event instanceof Event ? (event.target as HTMLInputElement).value : event;
        onChange({ type: 'raw', value });
    };

    renderGroups(options: SelectTypeOption[]) {
        const groupMap: Record<string, SelectTypeOption[]> = {};
        for (let opt of options) {
            const group = opt.group != null ? opt.group : 'Default';
            groupMap[group] = groupMap[group] || [];
            groupMap[group].push(opt);
        }

        return Object.keys(groupMap).map(label =>
            <optgroup key={label} label={label}>
                {this.renderOptions(groupMap[label])}
            </optgroup>,
        );
    }

    renderOptions(options: SelectTypeOption[]) {
        return options.map(({ value, label, id, props }) =>
            <option {...props} key={id} value={id}>{label || String(value)}</option>,
        );
    }

    render() {
        const {
            onChange,
            selectValue,
            value,
            options,
            label,
            rawOption,
            renderRawOption,
            rawOptionDefault,
            className,
            ...props
        } = this.props;

        const children = options.some(item => item.group) ?
            this.renderGroups(options) :
            this.renderOptions(options);

        return <div className={cx('select', className)} style={styles.root}>
            <div className='select-handle'>
                {label}
                <select
                    value={selectValue}
                    {...props}
                    tabIndex={-1}
                    onChange={this.handleChange}
                >
                    {children}
                </select>
            </div>

            {value.type !== 'raw' ? null :
                renderRawOption({ defaultValue: value.value, onChange: this.handleRawChange })
            }
        </div>;
    }
}

interface SelectTypeOwnProps {
    options: SelectTypeOption[];
    value?: SelectTypeValue | null;
    rawOption?: boolean;
    rawOptionDefault?: unknown;
    onChange: (value: { type: string; value?: unknown }) => void;
}

const SelectTypeConnected: ComponentType<any> = compose(
    defaultProps({
        rawOption: false,
        rawOptionDefault: '',
        renderRawOption: ({ onChange, defaultValue }: RawOptionConfig) =>
            <input key={String(defaultValue)} type='text' onBlur={onChange as React.FocusEventHandler} defaultValue={defaultValue as string} />,
    }),
    withPropsOnChange(['options', 'value'], (props: SelectTypeOwnProps & { rawOptionDefault?: unknown; rawOption?: boolean }) => {
        const value: SelectTypeValue = props.value == null ? { type: 'option', value: null } : props.value;

        let options: SelectTypeOption[] = props.options.map(item => ({
            ...item,
            type: item.type ?? 'option',
        }));

        if (props.rawOption) {
            const rawOpt: SelectTypeOption[] = [{
                type: 'raw',
                value: props.rawOptionDefault,
                label: 'Input value',
                group: 'Misc',
            }];
            options = rawOpt.concat(options);
        }

        let match = _.find(
            options,
            value.type === 'raw' ? { type: 'raw' } : _.pick(value, ['type', 'value']),
        ) as SelectTypeOption | undefined;

        if (value.value == null && !match) {
            match = {
                type: 'option',
                value: null,
                label: '[no value]',
                group: 'Misc',
                props: {
                    disabled: true,
                },
            };
            options = [match].concat(options);
        }

        options.forEach((opt, index) => { opt.id = '' + index; });

        const label = '' + (match!.label || match!.value);
        const selectValue = match!.id;

        return {
            selectValue,
            value,
            options,
            label,
        };
    }),
    // recompose cannot infer that the enhancers above supply SelectType's injected
    // props, so the base component is widened at this HOC boundary.
)(SelectType as ComponentType<any>) as ComponentType<any>;

export default SelectTypeConnected;
